import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppointmentType } from 'src/constants/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useCreateAppointmentTypeApi } from 'src/modules/calendar/api';
import { getRandomHexColor } from 'src/modules/settings/helpers';
import { useCampaignBuilderV2Api } from '../api';

interface UseCampaignCalendarsParams {
  /** Editing a saved campaign, which already carries its own calendar. */
  isExistingCampaign: boolean;
  /** Called when the company has exactly one calendar to offer. */
  onAutoSelect: (appointmentTypeId: string, calendarName: string) => void;
}

/**
 * The company's calendars, for the reminder campaign's picker.
 *
 * Two product rules live here rather than in the field that draws the select:
 *
 *  1. A company with no calendars cannot run a reminder campaign at all, so the
 *     builder creates a "Default" one on the spot.
 *  2. When there is exactly one to choose from, a new campaign takes it — an
 *     existing campaign already carries whichever it was saved with.
 */
export function useCampaignCalendars({
  isExistingCampaign,
  onAutoSelect,
}: UseCampaignCalendarsParams): AppointmentType[] {
  const companyId = useSelector(getCurrentCompanyId) as string;
  const api = useCampaignBuilderV2Api();
  const createAppointmentType = useCreateAppointmentTypeApi();
  const [calendars, setCalendars] = useState<AppointmentType[]>([]);
  // Held in a ref rather than a dependency: this effect creates a calendar, and
  // an unstable callback would re-run it on every render.
  const autoSelect = useRef(onAutoSelect);
  useEffect(() => { autoSelect.current = onAutoSelect; });

  useEffect(() => {
    let alive = true;
    if (!companyId) return undefined;

    api
      .getAppointmentTypes({
        companyId,
        limit: 10000,
        offset: 0,
        sort: 'campaign_id_desc',
        statuses: 'active,archived',
      })
      .then(async (res) => {
        if (!alive) return;
        const items = res.data.items ?? [];

        if (items.length === 0) {
          try {
            const created = await createAppointmentType('Default', getRandomHexColor(), companyId);
            if (!alive) return;
            setCalendars([created.data]);
            if (!isExistingCampaign) autoSelect.current(created.data.id, created.data.name);
          } catch (e) {
            console.error('Failed to create a default calendar', e);
          }
          return;
        }

        setCalendars(items);
        if (items.length === 1 && !isExistingCampaign) {
          autoSelect.current(items[0].id, items[0].name);
        }
      });

    return () => { alive = false; };
  }, [companyId, api, createAppointmentType, isExistingCampaign]);

  return calendars;
}
